import { fmtMoney, fmtDate } from "./format";
import QRCode from "qrcode";

// Only the existing business may fall back to Akash's original signature.
// Every other business uses its own validated image or an empty signature area.
export function businessLenderSignature(business) {
  const signature = String(business?.lender_signature || "");
  if (/^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(signature)) return signature;
  if (business?.account_business_id === "legacy") {
    return "https://akashfinance.online/finlend-lender-signature-white.png";
  }
  return "";
}

async function printWindow(html) {
  /*
   * FINLEND SERVER PDF PRINTING
   *
   * The existing document layout is sent unchanged to
   * FinLend's own backend.
   *
   * Server Chromium creates the A4 PDF.
   * pdf-lib then stamps the AF watermark physically
   * onto EVERY finished PDF page.
   */

  const w = window.open("", "_blank");

  if (!w) {
    alert(
      "Please allow pop-ups for FinLend to prepare the PDF."
    );
    return;
  }

  let printableHtml =
    String(html || "");

  let title =
    "FinLend Document";

  try {
    const titleMatch =
      printableHtml.match(
        /<title>([\s\S]*?)<\/title>/i
      );

    if (
      titleMatch &&
      titleMatch[1]
    ) {
      title =
        titleMatch[1]
          .replace(/<[^>]*>/g, "")
          .trim()
        || title;
    }
  } catch (_) {}

  /*
   * Open this immediately from the user's click.
   * This prevents iPhone Safari from blocking the
   * PDF window after the server request finishes.
   */
  try {
    w.document.open();

    w.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Preparing FinLend PDF</title>

        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
        />

        <style>
          body {
            margin: 0;
            min-height: 100vh;

            display: flex;
            align-items: center;
            justify-content: center;

            background: #f8fafc;

            font-family:
              -apple-system,
              BlinkMacSystemFont,
              "Segoe UI",
              Arial,
              sans-serif;

            color: #0b2545;
          }

          .box {
            text-align: center;
            padding: 30px;
          }

          .brand {
            color: #800020;
            font-weight: 800;
            letter-spacing: 1.5px;
            margin-bottom: 10px;
          }

          .message {
            font-size: 14px;
          }
        </style>
      </head>

      <body>
        <div class="box">
          <div class="brand">
            FINLEND
          </div>

          <div class="message">
            Preparing secure PDF…
          </div>
        </div>
      </body>
      </html>
    `);

    w.document.close();

  } catch (_) {}

  const token =
    localStorage.getItem("token");

  if (!token) {
    try {
      w.close();
    } catch (_) {}

    alert(
      "Your FinLend login session is not available. Please log in again."
    );

    return;
  }

  /*
   * ==========================================================
   * FINLEND UNIQUE DOCUMENT QR
   * ==========================================================
   */

  try {
    const parser = new DOMParser();

    const parsed = parser.parseFromString(
      printableHtml,
      "text/html"
    );

    const cleanText = (value) =>
      String(value || "")
        .replace(/\s+/g, " ")
        .trim();

    const getField = (wanted) => {
      const target =
        cleanText(wanted).toLowerCase();

      const fields =
        Array.from(
          parsed.querySelectorAll(".field")
        );

      for (const field of fields) {
        const label =
          cleanText(
            field.querySelector(".lbl")
              ?.textContent
          ).toLowerCase();

        if (
          label === target ||
          label.includes(target)
        ) {
          return cleanText(
            field.querySelector(".val")
              ?.textContent
          );
        }
      }

      return "";
    };

    const agreementNo =
      getField("Agreement No") ||
      getField("Agreement Number");

    const receiptNo =
      getField("Receipt No") ||
      getField("Receipt Number");

    const loanNo =
      getField("Loan No") ||
      getField("Loan Number");

    const customerName =
      getField("Customer") ||
      getField("Customer Name") ||
      getField("Borrower");

    const documentDate =
      getField("Agreement Date") ||
      getField("Paid Date") ||
      getField("Date");

    let reference =
      agreementNo ||
      receiptNo ||
      "";

    if (!reference) {
      const raw =
        printableHtml
          .replace(
            /Generated on[^<]*/gi,
            "Generated on"
          )
          .replace(
            /\/finlend-digitally-signed-qr\.png/gi,
            "FINLEND_QR"
          );

      const encoded =
        new TextEncoder().encode(raw);

      const digest =
        await crypto.subtle.digest(
          "SHA-256",
          encoded
        );

      const fingerprint =
        Array.from(
          new Uint8Array(digest)
        )
          .map((b) =>
            b.toString(16)
              .padStart(2, "0")
          )
          .join("")
          .slice(0, 20)
          .toUpperCase();

      reference =
        `DOC-${fingerprint}`;
    }

    let documentType =
      String(title || "FinLend Document")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    if (!documentType) {
      documentType =
        "finlend_document";
    }

    const registerResponse =
      await fetch(
        "/api/documents/register",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            "Authorization":
              `Bearer ${token}`,
          },

          body: JSON.stringify({
            document_type:
              documentType,

            reference,

            title,

            customer_name:
              customerName,

            loan_number:
              loanNo,

            document_date:
              documentDate,
          }),
        }
      );

    if (!registerResponse.ok) {
      throw new Error(
        "Unable to register FinLend document QR."
      );
    }

    const registered =
      await registerResponse.json();

    if (!registered?.token) {
      throw new Error(
        "Document QR token was not returned."
      );
    }

    const verifyUrl =
      `${window.location.origin}` +
      `/api/documents/verify/` +
      `${encodeURIComponent(
        registered.token
      )}`;

    const qrDataUrl =
      await QRCode.toDataURL(
        verifyUrl,
        {
          width: 260,
          margin: 1,
          errorCorrectionLevel: "M",
        }
      );

    printableHtml =
      printableHtml.replace(
        /src=["'][^"']*finlend-digitally-signed-qr\.png["']/i,
        `src="${qrDataUrl}"`
      );

  } catch (qrError) {
    try {
      w.close();
    } catch (_) {}

    alert(
      qrError?.message ||
      "Unable to create secure FinLend QR."
    );

    return;
  }


  fetch("/api/print/pdf", {
    method: "POST",

    headers: {
      "Content-Type":
        "application/json",

      "Authorization":
        `Bearer ${token}`,
    },

    body: JSON.stringify({
      html: printableHtml,
      title,
    }),
  })

    .then(async (response) => {

      if (!response.ok) {
        let message =
          "Unable to generate the FinLend PDF.";

        try {
          const data =
            await response.json();

          if (data?.detail) {
            message =
              String(data.detail);
          }

        } catch (_) {}

        /*
         * IMPORTANT:
         * Do NOT remove the user's login token here.
         *
         * A PDF error must never log the user out.
         */
        throw new Error(message);
      }

      return response.blob();
    })

    .then((blob) => {

      if (!blob) {
        throw new Error(
          "The PDF response was empty."
        );
      }

      const pdfUrl =
        URL.createObjectURL(blob);

      /*
       * The finished PDF already contains the
       * watermark on every real PDF page.
       */
      w.location.href = pdfUrl;

      /*
       * Leave sufficient time for iPhone/Android
       * PDF viewers to load the blob.
       */
      setTimeout(() => {
        try {
          URL.revokeObjectURL(
            pdfUrl
          );
        } catch (_) {}
      }, 10 * 60 * 1000);
    })

    .catch((err) => {

      console.error(
        "FinLend PDF generation error:",
        err
      );

      const safeMessage =
        String(
          err?.message
          || "Please try again."
        )
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");

      try {
        w.document.open();

        w.document.write(`
          <!DOCTYPE html>
          <html>
          <body
            style="
              font-family:Arial,sans-serif;
              padding:30px;
              color:#991b1b;
            "
          >
            <h3>
              Unable to prepare PDF
            </h3>

            <p>
              ${safeMessage}
            </p>
          </body>
          </html>
        `);

        w.document.close();

      } catch (_) {}

      alert(
        err?.message
        || "Unable to generate the FinLend PDF."
      );
    });
}
function brandHeader(business, settings) {
  const finlendLogo =
    typeof window !== "undefined"
      ? `${window.location.origin}/finlend-logo.png`
      : "/finlend-logo.png";

  const logo = `
    <img
      src="${finlendLogo}"
      alt="FinLend"
      style="width:64px;height:58px;object-fit:contain;display:block;"
    />
  `;

  const name = "FinLend";
  const lines = [];
  if (business?.owner_name) lines.push(business.owner_name);
  if (business?.address) lines.push(business.address);
  const contact = [];
  if (business?.mobile) contact.push("Mob: " + business.mobile);
  if (business?.email) contact.push(business.email);
  if (contact.length) lines.push(contact.join("  |  "));
  const reg = [];
  if (business?.gst_number) reg.push("GST: " + business.gst_number);
  if (business?.license_number) reg.push("Licence: " + business.license_number);
  if (reg.length) lines.push(reg.join("  |  "));
  return `
  <div class="brand">
    <div class="brand-identity">
      <div class="brand-logo">${logo}</div>
      <div class="brand-left">
        <div class="biz-name">${name}</div>
        <div class="biz-lines">${lines.map((l) => `<div>${l}</div>`).join("")}</div>
      </div>
    </div>
  </div>`;
}

function footer(business, settings) {
  const terms = settings?.print_show_terms && business?.terms
    ? `<div class="terms"><strong>Terms &amp; Conditions:</strong><br/>${business.terms.replace(/\n/g, "<br/>")}</div>`
    : "";

  const qrSrc =
    typeof window !== "undefined"
      ? `${window.location.origin}/finlend-digitally-signed-qr.png`
      : "/finlend-digitally-signed-qr.png";

  return `
  ${terms}
  <div class="foot">
    <div>
      Generated on ${fmtDate(new Date(), settings?.date_format)} ${new Date().toLocaleTimeString()}
    </div>

    <div class="digital-qr-block">
      <img
        src="${qrSrc}"
        alt="Digitally Signed QR"
        class="digital-qr-img"
      />
    </div>
  </div>`;
}

const BASE_CSS = `
  * { box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #0f172a; margin: 0; padding: 28px 32px; font-size: 13px; }
  .brand { display:flex; justify-content:flex-start; align-items:center; border-bottom:3px solid #800020; padding-bottom:12px; margin-bottom:18px; }
  .brand-identity { display:flex; align-items:center; gap:12px; }
  .brand-logo { flex:0 0 auto; }
  .brand-logo img { width:58px; height:58px; object-fit:contain; }
  .biz-name { font-size:24px; font-weight:800; color:#0b2545; letter-spacing:-0.5px; }
  .biz-lines { font-size: 11px; color:#475569; margin-top: 4px; line-height: 1.5; }
  .doc-title { font-size: 16px; font-weight: 700; color:#800020; text-transform: uppercase; letter-spacing: 1px; margin: 6px 0 16px; }
  .meta-row { display:flex; gap: 24px; flex-wrap: wrap; margin-bottom: 16px; }
  .field { margin-bottom: 6px; }
  .field .lbl { font-size: 10px; text-transform: uppercase; color:#64748b; letter-spacing: .5px; }
  .field .val { font-size: 13px; font-weight: 600; color:#0f172a; }
  .section-title { font-size: 12px; font-weight:700; text-transform: uppercase; letter-spacing:1px; color:#0b2545; margin: 20px 0 8px; border-left: 4px solid #800020; padding-left: 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  th { background:#0b2545; color:#fff; text-align:left; padding: 8px 10px; font-size: 11px; text-transform: uppercase; letter-spacing:.5px; }
  td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
  tr:nth-child(even) td { background:#f8fafc; }
  .badge { padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 700; }
  .paid { background:#dcfce7; color:#15803d; }
  .pending { background:#fef9c3; color:#a16207; }
  .overdue { background:#fee2e2; color:#b91c1c; }
  .summary-box { display:flex; gap: 12px; flex-wrap: wrap; margin: 8px 0 4px; }
  .sbox { flex:1; min-width: 130px; border:1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
  .sbox .k { font-size: 10px; text-transform: uppercase; color:#64748b; }
  .sbox .v { font-size: 16px; font-weight: 800; color:#0b2545; font-family:'JetBrains Mono',monospace; }
  .amt { font-family:'JetBrains Mono',monospace; font-weight:600; }
  .terms { margin-top: 24px; font-size: 10px; color:#475569; border-top:1px dashed #cbd5e1; padding-top: 10px; line-height:1.5; }
  .foot {
    display:flex;
    justify-content:space-between;
    align-items:flex-end;
    margin-top:28px;
    font-size:10px;
    color:#64748b;
    gap:16px;
  }

  .digital-qr-block {
    width:110px;
    flex:0 0 auto;
    text-align:center;
  }

  .digital-qr-img {
    width:82px;
    height:82px;
    object-fit:contain;
    display:block;
    margin:0 auto 4px;
  }

  .digital-qr-text {
    font-size:9px;
    font-weight:700;
    color:#0b2545;
  }
  @media print { body { padding: 0; } @page { margin: 14mm; } }
`;

function wrap(title, inner, business, settings) {
  return `<!DOCTYPE html><html><head><title>${title}</title><style>${BASE_CSS}</style></head><body>
    ${brandHeader(business, settings)}
    ${inner}
    ${footer(business, settings)}
  </body></html>`;
}

function statusBadge(inst) {
  if (inst.status === "paid") return `<span class="badge paid">PAID</span>`;
  if (inst.status === "partial") return `<span class="badge pending">PARTIAL</span>`;
  const overdue = new Date(inst.due_date) < new Date();
  return `<span class="badge ${overdue ? "overdue" : "pending"}">${overdue ? "OVERDUE" : "PENDING"}</span>`;
}


export function printLoanAgreement(agreement, customer, business, settings) {
  if (!agreement) {
    alert("Loan agreement details are not available.");
    return;
  }

  const cur = settings?.currency || "₹";
  const borrower = agreement.borrower || {};
  const loan = agreement.loan || {};
  const schedule = agreement.repayment_schedule || [];

  const money = (value) =>
    `${cur}${Number(value || 0).toLocaleString("en-IN")}`;

  const esc = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const photo = customer?.photo || "";

  // Always use the latest guarantor photo from the customer profile.
  // Fall back to agreement borrower data only if needed.
  const guarantorPhoto =
    customer?.guarantor_photo ||
    borrower?.guarantor_photo ||
    "";

  const scheduleRows = schedule.length
    ? schedule.map((row, index) => {
        const status = String(row.status || "pending").toLowerCase();
        const statusClass =
          status === "paid"
            ? "paid"
            : status === "overdue"
              ? "overdue"
              : "pending";

        return `
          <tr>
            <td>${esc(row.week || row.week_number || index + 1)}</td>
            <td>${esc(row.due_date || row.date || "—")}</td>
            <td class="amount">${money(
              row.emi || row.amount || row.installment || 0
            )}</td>
            <td>
              <span class="status ${statusClass}">
                ${esc(status.toUpperCase())}
              </span>
            </td>
          </tr>
        `;
      }).join("")
    : `
        <tr>
          <td colspan="4" class="empty">No repayment schedule available.</td>
        </tr>
      `;

  const lenderName = agreement.lender_name || business?.owner_name ||
    (business?.account_business_id === "legacy" ? "Akash Bhavani" : "");
  const lenderBusiness = "FinLend";
  const lenderSignature = businessLenderSignature(business);

  const finlendLogo =
    typeof window !== "undefined"
      ? `${window.location.origin}/finlend-logo.png`
      : "/finlend-logo.png";

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Loan Agreement - ${esc(agreement.agreement_number || "")}</title>

  <style>
    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #172033;
      font-family: "Helvetica Neue", Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
    }

    .page {
      width: 100%;
      max-width: 190mm;
      margin: 0 auto;
    }

    .top-line {
      height: 5px;
      background: #800020;
      margin-bottom: 14px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 1px solid #d9dee7;
      padding-bottom: 13px;
      margin-bottom: 16px;
    }

    .agreement-brand-wrap {
      display: flex;
      align-items: center;
      gap: 11px;
    }

    .agreement-logo {
      width: 50px;
      height: 50px;
      object-fit: contain;
      flex: 0 0 auto;
    }

    .brand {
      color: #800020;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .title {
      margin-top: 4px;
      color: #0b2545;
      font-size: 23px;
      font-weight: 800;
      letter-spacing: .3px;
    }

    .subtitle {
      margin-top: 3px;
      color: #0b2545;
      font-size: 10px;
    }

    .agreement-meta {
      text-align: right;
      min-width: 145px;
    }

    .meta-label {
      color: #0b2545;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .6px;
    }

    .meta-value {
      color: #0b2545;
      font-size: 12px;
      font-weight: 700;
      margin-bottom: 5px;
    }

    .parties {
      display: grid;
      grid-template-columns: 1.35fr 1fr;
      gap: 12px;
      margin-bottom: 15px;
    }

    .party {
      border: 1px solid #dce2ea;
      border-radius: 9px;
      padding: 12px;
      min-height: 120px;
    }

    .party-title {
      color: #800020;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 7px;
    }

    .party-body {
      display: flex;
      gap: 12px;
      align-items: flex-start;
    }

    .photo {
      width: 78px;
      height: 92px;
      object-fit: cover;
      border: 1px solid #cbd5e1;
      border-radius: 5px;
      background: #f8fafc;
      flex: 0 0 auto;
    }

    .photo-placeholder {
      width: 78px;
      height: 92px;
      border: 1px dashed #cbd5e1;
      border-radius: 5px;
      background: #f8fafc;
      color: #0b2545;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: 8px;
      flex: 0 0 auto;
    }

    .party-name {
      color: #0b2545;
      font-size: 16px;
      font-weight: 800;
      margin-bottom: 7px;
    }

    .detail {
      margin-top: 4px;
      color: #0b2545;
      font-size: 10px;
    }

    .detail strong {
      color: #334155;
    }

    .section {
      margin-top: 14px;
      page-break-inside: avoid;
    }

    .section-heading {
      color: #0b2545;
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: .8px;
      border-left: 4px solid #800020;
      padding-left: 7px;
      margin-bottom: 8px;
    }

    .loan-box {
      border: 1px solid #dce2ea;
      border-radius: 9px;
      overflow: hidden;
    }

    .loan-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
    }

    .loan-item {
      padding: 10px;
      border-right: 1px solid #e5e9ef;
      border-bottom: 1px solid #e5e9ef;
      min-height: 53px;
    }

    .loan-item:nth-child(4n) {
      border-right: none;
    }

    .loan-label {
      color: #0b2545;
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: .5px;
    }

    .loan-value {
      margin-top: 3px;
      color: #0b2545;
      font-size: 11px;
      font-weight: 800;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #dce2ea;
      border-radius: 8px;
      overflow: hidden;
    }

    thead {
      display: table-header-group;
    }

    th {
      background: #0b2545;
      color: #ffffff;
      padding: 8px 9px;
      text-align: left;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: .5px;
    }

    th:nth-child(3),
    td:nth-child(3) {
      text-align: right;
    }

    td {
      padding: 7px 9px;
      border-bottom: 1px solid #e5e9ef;
      font-size: 10px;
    }

    tr:nth-child(even) td {
      background: #f8fafc;
    }

    tr {
      page-break-inside: avoid;
    }

    .amount {
      font-weight: 700;
      font-family: "Courier New", monospace;
    }

    .status {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 12px;
      font-size: 8px;
      font-weight: 800;
    }

    .paid {
      background: #dcfce7;
      color: #15803d;
    }

    .pending {
      background: #fef9c3;
      color: #a16207;
    }

    .overdue {
      background: #fee2e2;
      color: #b91c1c;
    }

    .empty {
      text-align: center;
      color: #0b2545;
    }

    .terms {
      border: 1px solid #dce2ea;
      border-radius: 9px;
      padding: 12px 14px;
      color: #0b2545;
      font-size: 10px;
      line-height: 1.65;
      page-break-inside: avoid;
    }

    .terms-title {
      color: #0b2545;
      font-size: 11px;
      font-weight: 800;
      margin-bottom: 7px;
    }

    .terms p {
      margin: 5px 0;
    }

    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 45px;
      margin-top: 38px;
      page-break-inside: avoid;
    }

    .page1-signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 70px;
      margin-top: 32px;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .page1-signatures .signature-line {
      height: 38px;
      border-bottom: 1px solid #0b2545;
    }

    .page1-signatures .signature-title {
      margin-top: 6px;
      color: #0b2545;
      font-weight: 700;
      font-size: 10px;
    }

    .page1-signatures .signature-name {
      color: #0b2545;
      font-size: 9px;
      margin-top: 2px;
    }

    .signature-line {
      border-bottom: 1px solid #0b2545;
      height: 42px;
    }

    .signature-title {
      margin-top: 6px;
      color: #0b2545;
      font-weight: 700;
      font-size: 10px;
    }

    .signature-name {
      color: #0b2545;
      font-size: 9px;
      margin-top: 2px;
    }

    .footer {
      margin-top: 8px;
      padding-top: 9px;
      border-top: 1px solid #dce2ea;
      display: flex;
      justify-content: space-between;
      color: #0b2545;
      font-size: 8px;
    }

    .confidential {
      color: #800020;
      font-weight: 700;
      letter-spacing: .5px;
    }

    @page {
      size: A4;
      margin: 13mm;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .page {
        max-width: none;
        zoom: 0.99;
      }

      .no-print {
        display: none !important;
      }
    }
  </style>
</head>

<body>
  <div class="page">

    <div class="top-line"></div>

    <div class="header">
      <div class="agreement-brand-wrap">
        <img
          class="agreement-logo"
          src="${finlendLogo}"
          alt="FinLend"
        />

        <div>
          <div class="brand">${esc(lenderBusiness)}</div>
          <div class="title">LOAN AGREEMENT</div>
          <div class="subtitle">
            Loan agreement between the Borrower and the Lender
          </div>
        </div>
      </div>

      <div class="agreement-meta">
        <div class="meta-label">Agreement Number</div>
        <div class="meta-value">${esc(agreement.agreement_number || "—")}</div>

        <div class="meta-label">Agreement Date</div>
        <div class="meta-value">${esc(agreement.agreement_date || "—")}</div>
      </div>
    </div>

    <div class="parties">

      <div class="party">
        <div class="party-title">First Party — Borrower</div>

        <div class="party-body">
          ${
            photo
              ? `<img class="photo" src="${photo}" alt="Borrower Photo" />`
              : `<div class="photo-placeholder">CUSTOMER<br/>PHOTO</div>`
          }

          <div>
            <div class="party-name">${esc(borrower.name || "—")}</div>

            <div class="detail">
              <strong>Phone:</strong> ${esc(borrower.phone || "—")}
            </div>

            <div class="detail">
              <strong>${esc(borrower.id_type || "ID")} Number:</strong>
              ${esc(borrower.id_number || "—")}
            </div>

            <div class="detail">
              <strong>Address:</strong>
              ${esc(borrower.address || "—")}
            </div>
          </div>
        </div>
      </div>

      <div class="party">
        <div class="party-title">Second Party — Lender</div>

        <div class="party-name">${esc(lenderName)}</div>

        <div class="detail">
          <strong>Business:</strong> ${esc(lenderBusiness)}
        </div>

        <div class="detail">
          <strong>Collection Day:</strong>
          ${esc(loan.collection_day || "Friday")}
        </div>
      </div>

    </div>

    <div class="section">
      <div class="section-heading">Loan Details</div>

      <div class="loan-box">
        <div class="loan-grid">

          <div class="loan-item">
            <div class="loan-label">Loan Number</div>
            <div class="loan-value">${esc(agreement.loan_number || "—")}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Loan Amount</div>
            <div class="loan-value">${money(loan.principal)}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Processing Fee</div>
            <div class="loan-value">${money(loan.processing_fee || 0)}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Amount Given</div>
            <div class="loan-value">${money(loan.amount_given_to_customer)}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Total Repayment</div>
            <div class="loan-value">${money(loan.total_repayment)}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Weekly EMI</div>
            <div class="loan-value">${money(loan.weekly_emi)}</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Duration</div>
            <div class="loan-value">${esc(loan.duration_weeks || "—")} weeks</div>
          </div>

          <div class="loan-item">
            <div class="loan-label">Collection Day</div>
            <div class="loan-value">${esc(loan.collection_day || "Friday")}</div>
          </div>

        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-heading">Repayment Schedule</div>

      <table>
        <thead>
          <tr>
            <th>Week</th>
            <th>Due Date</th>
            <th>EMI</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          ${scheduleRows}
        </tbody>
      </table>
    </div>

    <div class="page1-signatures" style="margin-top:28px;">

      <div>
        <div style="height:${guarantorPhoto ? "78px" : "18px"};"></div>
        <div class="signature-line"></div>
        <div class="signature-title">Borrower / First Party</div>
        <div class="signature-name">${esc(borrower.name || "Borrower")}</div>
        <div class="signature-name">Signature</div>
        <div class="signature-name">Date: __________________</div>
      </div>

      <div>
        <div style="height:${guarantorPhoto ? "78px" : "18px"};display:flex;align-items:flex-end;justify-content:flex-start;">
          ${guarantorPhoto ? `
            <img
              src="${guarantorPhoto}"
              alt="Guarantor"
              style="
                width:54px;
                height:94px;
                object-fit:cover;
                border:1px solid #cbd5e1;
                border-radius:4px;
                display:block;
                margin-left:0;
                margin-bottom:7px;
              "
            />
          ` : ""}
        </div>

        <div class="signature-line"></div>
        <div class="signature-title">Guarantor (If Applicable)</div>
        <div class="signature-name">${esc(borrower.guarantor_name || "Guarantor")}</div>
        <div class="signature-name">Signature</div>
        <div class="signature-name">Date: __________________</div>
      </div>

    </div>

    <div class="section">
      <div class="terms">
        <div class="terms-title">
          TERMS &amp; CONDITIONS, DEFAULT AND REPAYMENT OBLIGATIONS
        </div>

        <p><strong>1. Loan Acceptance:</strong> The Borrower confirms that the loan amount, amount disbursed, processing fee, total repayment amount, instalment amount, repayment frequency and duration stated in this Agreement have been explained and accepted.</p>

        <p><strong>2. Repayment Obligation:</strong> The Borrower agrees to repay the total repayment amount strictly according to the repayment schedule attached to this Agreement and to make each instalment on the agreed collection day.</p>

        <p><strong>3. Timely Payment:</strong> Each instalment shall be paid on or before its scheduled due date. The Borrower is responsible for ensuring timely payment of all amounts due under this Agreement.</p>

        <p><strong>4. Default / Non-Payment:</strong> If any instalment is not paid when due, the unpaid amount shall remain outstanding and the account may be treated as overdue/default in FinLend records. Non-payment does not cancel, reduce or waive the Borrower's outstanding repayment obligation.</p>

        <p><strong>5. Notice of Default:</strong> In case of a missed or delayed payment, the Lender may issue reasonable reminders or written/digital notices requesting payment of the overdue amount and may provide an updated statement of outstanding dues.</p>

        <p><strong>6. Continued Default:</strong> If the Borrower continues to remain in default after reasonable notice, the Lender may require payment of the outstanding amount and may take appropriate lawful recovery or legal proceedings available under applicable law.</p>

        <p><strong>7. Lawful Recovery:</strong> No threat, intimidation, harassment, violence, public humiliation or other unlawful method shall be used for recovery. Any recovery action shall be carried out only in accordance with applicable law.</p>

        <p><strong>8. Partial Payment:</strong> A partial or delayed payment will be recorded against the loan and will not by itself discharge the remaining outstanding obligation.</p>

        <p><strong>9. Payment Records:</strong> Payments received from the Borrower shall be recorded in FinLend's records. The Borrower may request a statement showing payments received and the remaining outstanding amount.</p>

        <p><strong>10. Processing Fee and Charges:</strong> The processing fee and any other applicable charges shall be those disclosed to the Borrower and permitted by applicable law. No undisclosed or unauthorized charge shall be imposed.</p>

        <p><strong>11. Early Settlement / Prepayment:</strong> The Borrower may request early settlement of the loan. The settlement amount shall be calculated from the applicable loan records and terms, subject to applicable law.</p>

        <p><strong>12. False or Incorrect Information:</strong> The Borrower declares that the personal, address, identification and other information supplied to the Lender is true and correct. Materially false or misleading information may result in appropriate lawful action.</p>

        <p><strong>13. Change of Contact Details:</strong> The Borrower shall promptly inform the Lender of any material change in address, mobile number or other contact information.</p>

        <p><strong>14. Guarantor / Co-Obligant:</strong> Where a guarantor or co-obligant is recorded, the rights and obligations of such person shall be governed by the applicable documents and law.</p>

        <p><strong>15. Communication and Notices:</strong> Loan-related reminders, statements and notices may be communicated through permitted channels such as telephone, SMS, WhatsApp, email or written notice, subject to applicable law.</p>

        <p><strong>16. Loan Records:</strong> This Agreement, the loan details and the repayment schedule together record the understanding between the Borrower and the Lender for the loan identified above. Any amendment should be properly recorded.</p>

        <p><strong>17. Dispute Resolution:</strong> The parties shall first attempt to resolve any genuine dispute by reviewing the loan records and communicating with each other. If the dispute cannot be resolved, either party may approach the appropriate authority or competent court in accordance with applicable law.</p>

        <p><strong>18. Compliance With Law:</strong> This Agreement is subject to all applicable laws and regulations. If any provision conflicts with mandatory law, the mandatory legal requirement shall prevail.</p>

        <p><strong>19. Borrower Acknowledgement:</strong> The Borrower confirms that the loan details, repayment schedule and these Terms &amp; Conditions have been read, explained where necessary, understood and voluntarily accepted.</p>

        <p><strong>Important:</strong> Nothing in this Agreement authorizes any unlawful, coercive, threatening or harassing recovery method.</p>
      </div>
    </div>
<div class="signatures" style="grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top:8px; align-items:end;">

  <!-- BORROWER -->
  <div>
    <div style="height:72px;"></div>
    <div class="signature-line"></div>
    <div class="signature-title">Borrower / First Party</div>
    <div class="signature-name">${esc(borrower.name || "Borrower")}</div>
    <div class="signature-name">Signature</div>
    <div class="signature-name">Date: __________________</div>
  </div>

  <!-- GUARANTOR -->
  <div>
    <div style="height:92px;display:flex;align-items:flex-end;justify-content:center;padding-bottom:2px;">
      ${guarantorPhoto ? `
        <img
          src="${guarantorPhoto}"
          alt="Guarantor"
          style="
            width:64px;
            height:78px;
            object-fit:cover;
            border:1px solid #94a3b8;
            border-radius:3px;
            display:block;
          "
        />
      ` : ""}
    </div>

    <div class="signature-line"></div>
    <div class="signature-title">Guarantor (If Applicable)</div>
    <div class="signature-name">${esc(borrower.guarantor_name || "Guarantor")}</div>
    <div class="signature-name">Signature</div>
    <div class="signature-name">Date: __________________</div>
  </div>

  <!-- FINLEND -->
  <div style="position:relative;min-height:0;">

    <div style="
      height:118px;
      position:relative;
      display:flex;
      justify-content:center;
      align-items:flex-start;
    ">

      <img
        src="https://akashfinance.online/finlend-stamp.png"
        alt="FinLend Stamp"
        style="
          position:absolute;
          width:94px;
          height:94px;
          object-fit:contain;
          top:-8px;
          left:50%;
          transform:translateX(-50%) rotate(0deg);
          transform-origin:center;
          z-index:1;
        "
      />

      ${lenderSignature ? `
      <img
        src="${esc(lenderSignature)}"
        alt="Authorised Signature"
        style="
          position:absolute;
          width:118px;
          height:28px;
          object-fit:contain;
          background:transparent;
          left:50%;
          top:90px;
          transform:translateX(-50%);
          z-index:3;
        "
      />
      ` : ""}

    </div>

    <div class="signature-title" style="color:#123b9f;font-weight:700;text-align:center;margin-top:4px;">
      FinLend Private Limited
    </div>

    <div class="signature-name" style="color:#123b9f;font-weight:600;text-align:center;">
      Authorised Signatory
    </div>

    <div class="signature-name" style="color:#123b9f;font-weight:600;text-align:center;">
      Date: ${new Date().toLocaleDateString("en-IN")}
    </div>

  </div>

</div>

          <!--
            Hidden document metadata used by the FinLend
            secure QR registration system.
          -->
          <div style="display:none;">
            <div class="field">
              <div class="lbl">Agreement No</div>
              <div class="val">${esc(agreement.agreement_number || "")}</div>
            </div>

            <div class="field">
              <div class="lbl">Loan No</div>
              <div class="val">${esc(agreement.loan_number || "")}</div>
            </div>

            <div class="field">
              <div class="lbl">Customer</div>
              <div class="val">${esc(borrower.name || "")}</div>
            </div>

            <div class="field">
              <div class="lbl">Agreement Date</div>
              <div class="val">${esc(agreement.agreement_date || "")}</div>
            </div>
          </div>

          <div style="
            margin-top:0;
            display:flex;
            justify-content:space-between;
            align-items:flex-end;
            width:100%;
            font-size:8px;
            page-break-inside:avoid;
            break-inside:avoid;
            position:relative;
            top:34px;
          ">

            <div class="confidential" style="align-self:flex-end;">
              FINLEND — LOAN AGREEMENT
            </div>

            <div style="
              text-align:right;
              white-space:nowrap;
              align-self:flex-end;
              position:relative;
              min-width:110px;
            ">

              <!--
                Placeholder QR.
                printWindow() replaces this image with the
                UNIQUE QR for this exact agreement.
              -->
              <img
                src="/finlend-digitally-signed-qr.png"
                alt="FinLend Document QR"
                style="
                  position:absolute;
                  right:0;
                  top:-42px;
                  width:40px;
                  height:40px;
                  object-fit:contain;
                  display:block;
                "
              />

              <div>
                Generated on ${new Date().toLocaleString("en-IN")}
              </div>

            </div>

          </div>
    </div>

  </div>
</body>
</html>`;

  printWindow(html);
}


export function printCustomerProfile(customer, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;
  // Customer Profile PDF: print ACTIVE loans only.
  // CLOSED and RENEWED loans must not appear.
  const activeLoans = (customer.loans || []).filter((loan) => {
    const lifecycleStatus = String(
      loan.lifecycle_status || ""
    ).toLowerCase().trim();

    const status = String(
      loan.status || ""
    ).toLowerCase().trim();

    // Print ONLY genuinely active loans.
    // CLOSED and RENEWED loans are always excluded.
    return (
      status === "active" &&
      lifecycleStatus !== "closed" &&
      lifecycleStatus !== "renewed"
    );
  });

  const loansHtml = activeLoans.map((loan) => {
    const rows = (loan.schedule || []).map((i) => `
      <tr>
        <td>${i.week}</td>
        <td>${fmtDate(i.due_date, df)}</td>
        <td class="amt">${fmtMoney(i.amount, cur)}</td>
        <td class="amt">${fmtMoney(i.paid_amount, cur)}</td>
        <td>${i.paid_date ? fmtDate(i.paid_date, df) : "-"}</td>
        <td>${i.method || "-"}</td>
        <td>${statusBadge(i)}</td>
      </tr>`).join("");
    return `
    <div class="section-title">Loan ${loan.loan_number || "-"} &nbsp; (${String(loan.status || "ACTIVE").toUpperCase()})</div>
    <div class="summary-box">
      <div class="sbox"><div class="k">Loan Amount</div><div class="v">${fmtMoney(loan.principal, cur)}</div></div>
      <div class="sbox"><div class="k">Total Repayment</div><div class="v">${fmtMoney(loan.total_repayment, cur)}</div></div>
      <div class="sbox"><div class="k">Weekly EMI</div><div class="v">${fmtMoney(loan.weekly_emi, cur)}</div></div>
      <div class="sbox"><div class="k">Paid</div><div class="v">${fmtMoney(loan.total_paid, cur)}</div></div>
      <div class="sbox"><div class="k">Outstanding</div><div class="v">${fmtMoney(loan.outstanding, cur)}</div></div>
    </div>
    <div style="font-size:11px;color:#475569;margin:6px 0;">Start Date: <b>${fmtDate(loan.start_date, df)}</b> &nbsp;|&nbsp; Duration: <b>${loan.duration_weeks} weeks</b></div>
    <table>
      <thead><tr><th>Week</th><th>Due Date</th><th>EMI</th><th>Paid</th><th>Paid Date</th><th>Method</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  }).join("");

  const inner = `
    <div class="doc-title">Customer Profile &amp; Loan Statement</div>

    <div style="margin:10px 0 16px;padding:10px 12px;border:1px solid #cbd5e1;border-radius:6px;background:#f8fafc;font-size:11px;line-height:1.5;color:#334155;">
      <strong>Loan Agreement Declaration:</strong>
      The customer confirms that they have agreed to avail the loan from FinLend
      and accept the applicable loan amount, repayment schedule, and terms and
      conditions communicated by FinLend.
    </div>

    <div style="display:flex;gap:16px;align-items:flex-start;">
      ${customer.photo ? `<img src="${customer.photo}" style="height:84px;width:84px;object-fit:cover;border-radius:8px;border:1px solid #e2e8f0;" />` : ""}
      <div style="flex:1;">
        <div class="meta-row">
          <div class="field"><div class="lbl">Customer Name</div><div class="val">${customer.name}</div></div>
          <div class="field"><div class="lbl">Phone</div><div class="val">${customer.phone || "-"}</div></div>
          <div class="field"><div class="lbl">${customer.id_type || "ID"}</div><div class="val">${customer.id_number || "-"}</div></div>
        </div>
        <div class="field"><div class="lbl">Address</div><div class="val">${customer.address || "-"}</div></div>
      </div>
    </div>
    ${customer.guarantor_name ? `
      <div class="section-title">Guarantor</div>
      <div class="meta-row">
        <div class="field"><div class="lbl">Name</div><div class="val">${customer.guarantor_name}</div></div>
        <div class="field"><div class="lbl">Phone</div><div class="val">${customer.guarantor_phone || "-"}</div></div>
        <div class="field"><div class="lbl">Relation</div><div class="val">${customer.guarantor_relation || "-"}</div></div>
        <div class="field"><div class="lbl">Address</div><div class="val">${customer.guarantor_address || "-"}</div></div>
      </div>` : ""}
    ${customer.id_proof ? `<div class="section-title">ID Proof</div><img src="${customer.id_proof}" style="max-height:160px;max-width:280px;object-fit:contain;border:1px solid #e2e8f0;border-radius:8px;" />` : ""}
    ${loansHtml || '<p style="color:#64748b">No loans recorded.</p>'}
  `;
  printWindow(wrap("Customer Profile", inner, business, settings));
}

export function printReceipt(payment, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;
  const loan = payment.loan || {};
  const customer = payment.customer || {};
  const inst = (loan.schedule || []).find((i) => i.week === payment.week) || {};
  const inner = `
    <div class="doc-title">EMI Payment Receipt</div>
    <div class="meta-row">
      <div class="field"><div class="lbl">Receipt No</div><div class="val">${payment.receipt_no}</div></div>
      <div class="field"><div class="lbl">Loan No</div><div class="val">${loan.loan_number || "-"}</div></div>
      <div class="field"><div class="lbl">Week #</div><div class="val">${payment.week} of ${loan.duration_weeks || "-"}</div></div>
    </div>
    <div class="meta-row">
      <div class="field"><div class="lbl">Customer</div><div class="val">${customer.name || "-"}</div></div>
      <div class="field"><div class="lbl">Phone</div><div class="val">${customer.phone || "-"}</div></div>
    </div>
    <div class="summary-box">
      <div class="sbox"><div class="k">Due Date</div><div class="v" style="font-size:13px">${fmtDate(inst.due_date, df)}</div></div>
      <div class="sbox"><div class="k">Paid Date</div><div class="v" style="font-size:13px">${fmtDate(payment.paid_date, df)}</div></div>
      <div class="sbox"><div class="k">Method</div><div class="v" style="font-size:13px">${payment.method}</div></div>
    </div>
    <div class="summary-box">
      <div class="sbox" style="background:#f0fdf4;border-color:#bbf7d0"><div class="k">Amount Paid</div><div class="v" style="color:#15803d">${fmtMoney(payment.amount, cur)}</div></div>
      <div class="sbox"><div class="k">Total Paid Till Date</div><div class="v">${fmtMoney(payment.total_paid, cur)}</div></div>
      <div class="sbox" style="background:#fef2f2;border-color:#fecaca"><div class="k">Remaining Balance</div><div class="v" style="color:#b91c1c">${fmtMoney(payment.remaining_balance, cur)}</div></div>
    </div>
  `;
  printWindow(wrap("EMI Receipt", inner, business, settings));
}

export function printIdProof(customer, business, settings) {
  if (!customer.id_proof) return;
  const inner = `
    <div class="doc-title">Customer ID Proof</div>
    <div class="meta-row">
      <div class="field"><div class="lbl">Customer Name</div><div class="val">${customer.name}</div></div>
      <div class="field"><div class="lbl">Phone</div><div class="val">${customer.phone || "-"}</div></div>
      <div class="field"><div class="lbl">${customer.id_type || "ID"} Number</div><div class="val">${customer.id_number || "-"}</div></div>
    </div>
    <div class="section-title">${customer.id_type || "ID"} Document</div>
    <div style="text-align:center;margin-top:8px;">
      <img src="${customer.id_proof}" style="max-width:100%;max-height:620px;object-fit:contain;border:1px solid #e2e8f0;border-radius:8px;" />
    </div>
  `;
  printWindow(wrap("Customer ID Proof", inner, business, settings));
}

export function printPLReport(report, periodLabel, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;
  const expenseRows = (report.expenses || []).map((e) => `
    <tr><td>${fmtDate(e.date, df)}</td><td>${e.title}</td><td>${e.category}</td><td class="amt">${fmtMoney(e.amount, cur)}</td></tr>
  `).join("") || `<tr><td colspan="4" style="color:#64748b">No expenses recorded.</td></tr>`;
  const row = (k, v, color) => `
    <div class="sbox"><div class="k">${k}</div><div class="v" ${color ? `style="color:${color}"` : ""}>${fmtMoney(v, cur)}</div></div>`;
  const inner = `
    <div class="doc-title">Profit &amp; Loss Statement</div>
    <div style="font-size:12px;color:#475569;margin-bottom:12px;">Period: <b>${periodLabel}</b> &nbsp; (${fmtDate(report.start, df)} – ${fmtDate(report.end, df)})</div>
    <div class="summary-box">
      ${row("Today's Collection", report.today_collection)}
        ${row("Weekly Collection", report.weekly_collection)}
        ${row("Total Money Lent", report.total_lent)}
      ${row("Repayments Received", report.repayments_received)}
      ${row("Interest / Profit", report.interest_profit, "#15803d")}
    </div>
    <div class="summary-box">
      ${row("Outstanding", report.outstanding)}
      ${row("Overdue", report.overdue, "#b91c1c")}
      ${row("Total Expenses", report.total_expenses, "#b91c1c")}
    </div>
    <div class="summary-box">
      <div class="sbox" style="background:${report.net_profit_loss >= 0 ? "#f0fdf4" : "#fef2f2"};border-color:${report.net_profit_loss >= 0 ? "#bbf7d0" : "#fecaca"};flex:1">
        <div class="k">Net Profit / Loss</div>
        <div class="v" style="color:${report.net_profit_loss >= 0 ? "#15803d" : "#b91c1c"};font-size:22px">${fmtMoney(report.net_profit_loss, cur)}</div>
      </div>
    </div>
    <div class="section-title">Expenses Breakdown</div>
    <table>
      <thead><tr><th>Date</th><th>Title</th><th>Category</th><th>Amount</th></tr></thead>
      <tbody>${expenseRows}</tbody>
    </table>
  `;
  printWindow(wrap("Profit & Loss Report", inner, business, settings));
}

export function printRepaymentsReceived(rows, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows = Array.isArray(rows) ? rows : [];

  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalAmountPaid = 0;

  const tableRows = safeRows.map((row, index) => {
    const principal = Number(row.principal) || 0;
    const interest = Number(row.interest) || 0;
    const amountPaid = Number(row.amount_paid) || 0;

    totalPrincipal += principal;
    totalInterest += interest;
    totalAmountPaid += amountPaid;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(principal, cur)}</td>
        <td class="amt">${fmtMoney(interest, cur)}</td>
        <td class="amt">${fmtMoney(amountPaid, cur)}</td>
      </tr>`;
  }).join("");

  const inner = `
    <div class="doc-title">Repayments Received</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Principal</th>
          <th>Interest</th>
          <th>Amount Paid</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows || `
          <tr>
            <td colspan="7" style="text-align:center;color:#64748b;">
              No repayment records found.
            </td>
          </tr>
        `}
        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL</td>
            <td class="amt">${fmtMoney(totalPrincipal, cur)}</td>
            <td class="amt">${fmtMoney(totalInterest, cur)}</td>
            <td class="amt">${fmtMoney(totalAmountPaid, cur)}</td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(wrap("Repayments Received", inner, business, settings));
}









export function printPrincipalRecyclingHistory(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(b?.date || "").localeCompare(
      String(a?.date || "")
    )
  );

  const tableRows = safeRows.map((row, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
      <td class="amt">${fmtMoney(Number(row.amount) || 0, cur)}</td>
      <td>${row.notes || "—"}</td>
    </tr>
  `).join("");

  const inner = `
    <div class="doc-title">
      Principal Recycling History
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Date</th>
          <th>Amount</th>
          <th>Notes</th>
        </tr>
      </thead>

      <tbody>
        ${tableRows || `
          <tr>
            <td
              colspan="4"
              style="text-align:center;color:#64748b;"
            >
              No principal recycling history found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="2">TOTAL</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
            <td></td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Principal Recycling History",
      inner,
      business,
      settings
    )
  );
}


export function printInterestRecyclingHistory(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(b?.date || "").localeCompare(
      String(a?.date || "")
    )
  );

  const tableRows = safeRows.map((row, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
      <td class="amt">${fmtMoney(Number(row.amount) || 0, cur)}</td>
      <td>${row.notes || "—"}</td>
    </tr>
  `).join("");

  const inner = `
    <div class="doc-title">
      Interest Recycling History
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Date</th>
          <th>Amount</th>
          <th>Notes</th>
        </tr>
      </thead>

      <tbody>
        ${tableRows || `
          <tr>
            <td
              colspan="4"
              style="text-align:center;color:#64748b;"
            >
              No interest recycling history found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="2">TOTAL</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
            <td></td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Interest Recycling History",
      inner,
      business,
      settings
    )
  );
}


export function printRenewedLoans(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${row.customer_name || "Unknown"}</td>
      <td>${row.renewal_date ? fmtDate(row.renewal_date, df) : "—"}</td>
      <td>${row.loan_number || "—"}</td>
      <td>${row.old_loan_number || "—"}</td>
      <td class="amt">${fmtMoney(Number(row.old_amount) || 0, cur)}</td>
      <td class="amt">${fmtMoney(Number(row.new_amount) || 0, cur)}</td>
      <td class="amt">${fmtMoney(Number(row.amount_given) || 0, cur)}</td>
    </tr>
  `).join("");

  const inner = `
    <div class="doc-title">Renewed Loans Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Renewal Date</th>
          <th>New Loan No</th>
          <th>Old Loan No</th>
          <th>Old Amount</th>
          <th>New Loan Amount</th>
          <th>Amount Given</th>
        </tr>
      </thead>

      <tbody>
        ${tableRows || `
          <tr>
            <td
              colspan="8"
              style="text-align:center;color:#64748b;"
            >
              No renewed loan records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="7">TOTAL AMOUNT GIVEN</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Renewed Loans Report",
      inner,
      business,
      settings
    )
  );
}


export function printOverdueCustomers(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const overdue =
      Number(row.overdue) || 0;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown Customer"}</td>
        <td>${row.phone || "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(overdue, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Overdue Customers Report</div>

    <div style="
      margin:0 0 14px 0;
      padding:10px 12px;
      border:1px solid #dbe3ec;
      border-radius:8px;
      background:#f8fafc;
      font-size:13px;
    ">
      <strong>Total Overdue Customers:</strong>
      ${safeRows.length}
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Phone</th>
          <th>Loan Number</th>
          <th>Overdue</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="5"
              style="text-align:center;color:#64748b;"
            >
              No overdue customers found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="
            font-weight:800;
            border-top:2px solid #0b2545;
          ">
            <td colspan="4">TOTAL OVERDUE</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Overdue Customers Report",
      inner,
      business,
      settings
    )
  );
}


export function printExpenses(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows = Array.isArray(rows)
    ? [...rows]
    : [];

  safeRows.sort((a, b) =>
    String(b?.date || "").localeCompare(
      String(a?.date || "")
    )
  );

  const tableRows = safeRows.map((row, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
      <td>${row.title || "—"}</td>
      <td>${row.category || "—"}</td>
      <td class="amt">${fmtMoney(Number(row.amount) || 0, cur)}</td>
    </tr>
  `).join("");

  const inner = `
    <div class="doc-title">Expenses Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Date</th>
          <th>Title</th>
          <th>Category</th>
          <th>Amount</th>
        </tr>
      </thead>

      <tbody>
        ${tableRows || `
          <tr>
            <td colspan="5"
                style="text-align:center;color:#64748b;">
              No expenses recorded.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL EXPENSES</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Expenses Report",
      inner,
      business,
      settings
    )
  );
}


export function printTotalMoneyLent(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const rowDate =
      row.date ||
      row.start_date ||
      row.loan_date ||
      row.renewal_date ||
      "";

    const amount = Number(
      row.amount ??
      row.amount_lent ??
      row.money_lent ??
      row.disbursed_amount ??
      row.amount_disbursed ??
      row.amount_given ??
      row.principal ??
      row.new_amount ??
      0
    ) || 0;

    const type =
      row.type ||
      row.loan_type ||
      (
        row.is_renewal ||
        row.renewed_from_loan_id
          ? "Renewal"
          : "Loan"
      );

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${rowDate ? fmtDate(rowDate, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td>${type || "Loan"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Total Money Lent Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Type</th>
          <th>Amount Lent</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="6"
              style="text-align:center;color:#64748b;"
            >
              No money lent records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="5">TOTAL MONEY LENT</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Total Money Lent Report",
      inner,
      business,
      settings
    )
  );
}


export function printInterestProfit(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const rowDate =
      row.start_date ||
      row.date ||
      "";

    const amount =
      Number(row.amount) || 0;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${rowDate ? fmtDate(rowDate, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Interest / Profit Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Interest / Profit</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="5"
              style="text-align:center;color:#64748b;"
            >
              No interest / profit records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Interest / Profit Report",
      inner,
      business,
      settings
    )
  );
}


export function printPrincipalCollected(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const amount =
      Number(
        row.principal_collected ??
        row.principal ??
        0
      ) || 0;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Principal Collected Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Loan No</th>
          <th>Principal Collected</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="4"
              style="text-align:center;color:#64748b;"
            >
              No principal collection records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="3">TOTAL</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Principal Collected Report",
      inner,
      business,
      settings
    )
  );
}


export function printInterestCollected(
  rows,
  authoritativeTotal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const amount =
      Number(
        row.interest_collected ??
        row.interest ??
        0
      ) || 0;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Interest Collected Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Loan No</th>
          <th>Interest Collected</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="4"
              style="text-align:center;color:#64748b;"
            >
              No interest collection records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="3">TOTAL</td>
            <td class="amt">
              ${fmtMoney(authoritativeTotal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Interest Collected Report",
      inner,
      business,
      settings
    )
  );
}


export function printOutstandingBalance(
  rows,
  totalPrincipal,
  totalInterest,
  totalOutstanding,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const principal = Number(
      row.principal_outstanding ??
      row.remaining_principal ??
      row.principal ??
      0
    ) || 0;

    const interest = Number(
      row.interest_outstanding ??
      row.remaining_interest ??
      row.interest ??
      0
    ) || 0;

    const total = Number(
      row.total_outstanding ??
      row.outstanding ??
      row.amount ??
      (principal + interest)
    ) || 0;

    const rowDate =
      row.date ||
      row.start_date ||
      "";

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${rowDate ? fmtDate(rowDate, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(principal, cur)}</td>
        <td class="amt">${fmtMoney(interest, cur)}</td>
        <td class="amt">${fmtMoney(total, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Outstanding Balance Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Principal Outstanding</th>
          <th>Interest Outstanding</th>
          <th>Total Outstanding</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td colspan="7"
                style="text-align:center;color:#64748b;">
              No outstanding records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL</td>

            <td class="amt">
              ${fmtMoney(totalPrincipal, cur)}
            </td>

            <td class="amt">
              ${fmtMoney(totalInterest, cur)}
            </td>

            <td class="amt">
              ${fmtMoney(totalOutstanding, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Outstanding Balance Report",
      inner,
      business,
      settings
    )
  );
}


export function printPrincipalOutstanding(
  rows,
  totalPrincipal,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const amount = Number(
      row.remaining_principal ??
      row.principal_outstanding ??
      row.amount ??
      0
    ) || 0;

    const rowDate =
      row.start_date ||
      row.date ||
      "";

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${rowDate ? fmtDate(rowDate, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Principal Outstanding Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Principal Outstanding</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td colspan="5"
                style="text-align:center;color:#64748b;">
              No principal outstanding records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL</td>

            <td class="amt">
              ${fmtMoney(totalPrincipal, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Principal Outstanding Report",
      inner,
      business,
      settings
    )
  );
}


export function printInterestOutstanding(
  rows,
  totalInterest,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const tableRows = safeRows.map((row, index) => {

    const amount = Number(
      row.amount ??
      row.interest_outstanding ??
      row.remaining_interest ??
      0
    ) || 0;

    const rowDate =
      row.date ||
      row.start_date ||
      "";

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${rowDate ? fmtDate(rowDate, df) : "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">${fmtMoney(amount, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Interest Outstanding Report</div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Interest Outstanding</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td colspan="5"
                style="text-align:center;color:#64748b;">
              No interest outstanding records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="4">TOTAL</td>

            <td class="amt">
              ${fmtMoney(totalInterest, cur)}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Interest Outstanding Report",
      inner,
      business,
      settings
    )
  );
}


export function printTodayCollection(
  rows,
  startDate,
  endDate,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(
      a?.customer_name || ""
    ).localeCompare(
      String(
        b?.customer_name || ""
      ),
      undefined,
      {
        sensitivity: "base",
      }
    )
  );

  let totalPrincipal = 0;
  let totalInterest = 0;
  let grandTotal = 0;

  const tableRows = safeRows
    .map((row, index) => {

      const principal =
        Number(
          row.principal_collected
        ) || 0;

      const interest =
        Number(
          row.interest_collected
        ) || 0;

      const total =
        principal + interest;

      totalPrincipal += principal;
      totalInterest += interest;
      grandTotal += total;

      return `
        <tr>
          <td>${index + 1}</td>
          <td>${row.customer_name || "Unknown"}</td>
          <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
          <td>${row.loan_number || "—"}</td>
          <td class="amt">${fmtMoney(principal, cur)}</td>
          <td class="amt">${fmtMoney(interest, cur)}</td>
          <td class="amt">${fmtMoney(total, cur)}</td>
        </tr>
      `;
    })
    .join("");

  const periodText =
    startDate && endDate
      ? (
          startDate === endDate
            ? fmtDate(startDate, df)
            : `${fmtDate(startDate, df)} – ${fmtDate(endDate, df)}`
        )
      : "—";

  const inner = `
    <div class="doc-title">Today's Collection Report</div>

    <div style="
      margin:0 0 14px 0;
      padding:10px 12px;
      border:1px solid #dbe3ec;
      border-radius:8px;
      background:#f8fafc;
      font-size:13px;
    ">
      <strong>Selected Period:</strong>
      ${periodText}
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Principal</th>
          <th>Interest</th>
          <th>Total</th>
        </tr>
      </thead>

      <tbody>

        ${tableRows || `
          <tr>
            <td
              colspan="7"
              style="text-align:center;color:#64748b;"
            >
              No collection records found.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="
            font-weight:800;
            border-top:2px solid #0b2545;
          ">
            <td colspan="4">TOTAL</td>

            <td class="amt">
              ${fmtMoney(
                totalPrincipal,
                cur
              )}
            </td>

            <td class="amt">
              ${fmtMoney(
                totalInterest,
                cur
              )}
            </td>

            <td class="amt">
              ${fmtMoney(
                grandTotal,
                cur
              )}
            </td>
          </tr>
        ` : ""}

      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Today's Collection Report",
      inner,
      business,
      settings
    )
  );
}


export function printTodaysEmi(rows, selectedDate, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows = Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalEmi = 0;

  const tableRows = safeRows.map((row, index) => {
    const principal = Number(row.principal_amount) || 0;
    const interest = Number(row.interest_amount) || 0;
    const emi = Number(row.emi_amount) || 0;

    totalPrincipal += principal;
    totalInterest += interest;
    totalEmi += emi;

    return `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown"}</td>
        <td>${row.phone_number || "—"}</td>
        <td>${row.loan_number || "—"}</td>
        <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
        <td class="amt">${fmtMoney(principal, cur)}</td>
        <td class="amt">${fmtMoney(interest, cur)}</td>
        <td class="amt">${fmtMoney(emi, cur)}</td>
      </tr>
    `;
  }).join("");

  const inner = `
    <div class="doc-title">Today's EMI Report</div>

    <div style="
      margin:0 0 14px 0;
      padding:10px 12px;
      border:1px solid #dbe3ec;
      border-radius:8px;
      background:#f8fafc;
      font-size:13px;
    ">
      <strong>Selected Date:</strong>
      ${selectedDate ? fmtDate(selectedDate, df) : "—"}
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Phone Number</th>
          <th>Loan No</th>
          <th>Date</th>
          <th>Principal</th>
          <th>Interest</th>
          <th>EMI Amount</th>
        </tr>
      </thead>

      <tbody>
        ${tableRows || `
          <tr>
            <td colspan="8" style="text-align:center;color:#64748b;">
              No EMI due on this date.
            </td>
          </tr>
        `}

        ${safeRows.length ? `
          <tr style="font-weight:800;border-top:2px solid #0b2545;">
            <td colspan="5">TOTAL</td>
            <td class="amt">${fmtMoney(totalPrincipal, cur)}</td>
            <td class="amt">${fmtMoney(totalInterest, cur)}</td>
            <td class="amt">${fmtMoney(totalEmi, cur)}</td>
          </tr>
        ` : ""}
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Today's EMI Report",
      inner,
      business,
      settings
    )
  );
}


export function printBalanceSheet(report, business, settings) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const amount = (value) => fmtMoney(Number(value) || 0, cur);

  const inner = `
    <div class="doc-title">Balance Sheet</div>

    <div style="font-size:12px;color:#475569;margin-bottom:18px;">
      As on: <b>${fmtDate(report.as_on, df)}</b>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px;">

      <div>
        <div class="section-title">Assets</div>
        <table>
          <tbody>
            <tr><td>Cash in Hand</td><td class="amt" style="text-align:right">${amount(report.assets.cash_in_hand)}</td></tr>
            <tr><td>Bank Balance</td><td class="amt" style="text-align:right">${amount(report.assets.bank_balance)}</td></tr>
            <tr><td>UPI Balance</td><td class="amt" style="text-align:right">${amount(report.assets.upi_balance)}</td></tr>
            <tr><td>Principal Outstanding</td><td class="amt" style="text-align:right">${amount(report.assets.principal_outstanding)}</td></tr>
            <tr><td>Interest Receivable</td><td class="amt" style="text-align:right">${amount(report.assets.interest_receivable)}</td></tr>
            <tr><td>Other Assets</td><td class="amt" style="text-align:right">${amount(report.assets.other_assets)}</td></tr>
            <tr style="font-weight:800;border-top:2px solid #0b2545;">
              <td>Total Assets</td>
              <td class="amt" style="text-align:right">${amount(report.assets.total_assets)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <div class="section-title">Liabilities</div>
        <table>
          <tbody>
            <tr><td>Business Borrowings</td><td class="amt" style="text-align:right">${amount(report.liabilities.business_borrowings)}</td></tr>
            <tr><td>Other Payables</td><td class="amt" style="text-align:right">${amount(report.liabilities.other_payables)}</td></tr>
            <tr><td>Other Liabilities</td><td class="amt" style="text-align:right">${amount(report.liabilities.other_liabilities)}</td></tr>
            <tr style="font-weight:800;border-top:2px solid #0b2545;">
              <td>Total Liabilities</td>
              <td class="amt" style="text-align:right">${amount(report.liabilities.total_liabilities)}</td>
            </tr>
          </tbody>
        </table>

        <div class="section-title">Owner's Equity</div>
        <table>
          <tbody>
            <tr><td>Owner Capital</td><td class="amt" style="text-align:right">${amount(report.equity.owner_capital)}</td></tr>
            <tr><td>Accumulated Profit</td><td class="amt" style="text-align:right">${amount(report.equity.accumulated_profit)}</td></tr>
            <tr><td>Less: Drawings</td><td class="amt" style="text-align:right">${amount(report.equity.drawings)}</td></tr>
            <tr style="font-weight:800;border-top:2px solid #0b2545;">
              <td>Total Equity</td>
              <td class="amt" style="text-align:right">${amount(report.equity.total_equity)}</td>
            </tr>
            <tr style="font-weight:800;border-top:3px solid #0b2545;">
              <td>Liabilities + Equity</td>
              <td class="amt" style="text-align:right">${amount(report.total_liabilities_and_equity)}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    ${report.dashboard_reference ? `
      <div style="margin-top:22px;">
        <div class="section-title">FinLend Loan &amp; Collection Summary</div>
        <table>
          <tbody>
            <tr><td>Total Money Lent</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.total_money_lent)}</td></tr>
            <tr><td>Repayments Received</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.repayments_received)}</td></tr>
            <tr><td>Interest / Loan Profit</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.interest_profit)}</td></tr>
            <tr><td>Principal Collected</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.principal_collected)}</td></tr>
            <tr><td>Interest Collected</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.interest_collected)}</td></tr>
            <tr><td>Cash Collected</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.cash_collected)}</td></tr>
            <tr><td>UPI Collected</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.upi_collected)}</td></tr>
            <tr><td>Principal Outstanding</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.principal_outstanding)}</td></tr>
            <tr><td>Interest Outstanding</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.interest_outstanding)}</td></tr>
            <tr style="font-weight:800;border-top:2px solid #0b2545;">
              <td>Total Outstanding</td>
              <td class="amt" style="text-align:right">${amount(report.dashboard_reference.outstanding)}</td>
            </tr>
            <tr><td>Total Expenses</td><td class="amt" style="text-align:right">${amount(report.dashboard_reference.total_expenses)}</td></tr>
            <tr style="font-weight:800;">
              <td>Net Profit After Expenses</td>
              <td class="amt" style="text-align:right">${amount(report.dashboard_reference.net_profit)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      ${report.reconciliation ? `
        <div style="margin-top:22px;">
          <div class="section-title">FinLend Reconciliation</div>
          <table>
            <tbody>
              <tr>
                <td>Gross: Money Lent + Interest Profit</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.gross_left)}</td>
              </tr>
              <tr>
                <td>Gross: Outstanding + Repayments Received</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.gross_right)}</td>
              </tr>
              <tr style="font-weight:800;">
                <td>Gross Difference</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.gross_difference)}</td>
              </tr>
              <tr>
                <td>Net: Money Lent + Net Profit</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.net_left)}</td>
              </tr>
              <tr>
                <td>Net: Outstanding + Collections − Expenses</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.net_right)}</td>
              </tr>
              <tr style="font-weight:800;">
                <td>Net Difference</td>
                <td class="amt" style="text-align:right">${amount(report.reconciliation.net_difference)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : ""}
    ` : ""}

    <div style="
      margin-top:18px;
      padding:12px 16px;
      border:1px solid ${report.is_balanced ? "#bbf7d0" : "#fecaca"};
      background:${report.is_balanced ? "#f0fdf4" : "#fef2f2"};
      border-radius:8px;
      display:flex;
      justify-content:space-between;
      align-items:center;
    ">
      <div>
        <div style="font-size:10px;text-transform:uppercase;color:#64748b;letter-spacing:.5px;">
          Balance Check
        </div>
        <div style="
          margin-top:3px;
          font-weight:800;
          color:${report.is_balanced ? "#15803d" : "#b91c1c"};
        ">
          ${report.is_balanced ? "BALANCE SHEET BALANCED" : "BALANCE SHEET NOT BALANCED"}
        </div>
      </div>

      <div style="text-align:right;">
        <div style="font-size:10px;color:#64748b;">Difference</div>
        <div class="amt" style="font-weight:800;">
          ${amount(report.balance_difference)}
        </div>
      </div>
    </div>
  `;

  printWindow(wrap("Balance Sheet", inner, business, settings));
}


export function printActiveLoans(
  rows,
  business,
  settings
) {
  const cur = settings?.currency || "₹";
  const df = settings?.date_format;

  const safeRows =
    Array.isArray(rows) ? [...rows] : [];

  safeRows.sort((a, b) =>
    String(a?.customer_name || "").localeCompare(
      String(b?.customer_name || ""),
      undefined,
      { sensitivity: "base" }
    )
  );

  const totalAmount = safeRows.reduce(
    (sum, row) =>
      sum + (Number(row?.amount) || 0),
    0
  );

  const tableRows = safeRows.map(
    (row, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${row.customer_name || "Unknown Customer"}</td>
        <td>
          ${
            row.start_date
              ? fmtDate(row.start_date, df)
              : "—"
          }
        </td>
        <td>${row.loan_number || "—"}</td>
        <td class="amt">
          ${fmtMoney(Number(row.amount) || 0, cur)}
        </td>
      </tr>
    `
  ).join("");

  const inner = `
    <div class="doc-title">
      Total Active Loans Report
    </div>

    <div style="
      margin:0 0 14px 0;
      padding:10px 12px;
      border:1px solid #dbe3ec;
      border-radius:8px;
      background:#f8fafc;
      font-size:13px;
    ">
      <strong>Total Active Loans:</strong>
      ${safeRows.length}
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Customer Name</th>
          <th>Date</th>
          <th>Loan No</th>
          <th>Amount</th>
        </tr>
      </thead>

      <tbody>
        ${
          tableRows || `
            <tr>
              <td
                colspan="5"
                style="
                  text-align:center;
                  color:#64748b;
                "
              >
                No active loans found.
              </td>
            </tr>
          `
        }

        ${
          safeRows.length ? `
            <tr style="
              font-weight:800;
              border-top:2px solid #0b2545;
            ">
              <td colspan="4">
                TOTAL
              </td>

              <td class="amt">
                ${fmtMoney(totalAmount, cur)}
              </td>
            </tr>
          ` : ""
        }
      </tbody>
    </table>
  `;

  printWindow(
    wrap(
      "Total Active Loans Report",
      inner,
      business,
      settings
    )
  );
}


export function printMonthlyLoanSummary(
  report,
  monthTitle,
  business,
  settings
) {
  if (!report) {
    alert("Monthly loan summary is not available.");
    return;
  }

  const cur = settings?.currency || "₹";
  const df = settings?.date_format;
  const rows = Array.isArray(report.rows) ? report.rows : [];

  const renewalTotal = rows
    .filter((row) => row.type === "Renewal")
    .reduce(
      (sum, row) =>
        sum + Number(row.renewal_amount || 0),
      0
    );

  const tableRows = rows
    .map(
      (row, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${row.date ? fmtDate(row.date, df) : "—"}</td>
          <td>${row.customer_name || "Unknown"}</td>
          <td>${row.loan_number || "—"}</td>
          <td>${row.type || "—"}</td>
          <td class="amt">${fmtMoney(row.principal || 0, cur)}</td>
          <td class="amt">${fmtMoney(row.interest || 0, cur)}</td>
          <td class="amt">${
            row.type === "Renewal"
              ? fmtMoney(row.renewal_amount || 0, cur)
              : "—"
          }</td>
        </tr>
      `
    )
    .join("");

  const stat = (label, value, money = false) => `
    <div class="sbox">
      <div class="k">${label}</div>
      <div class="v">
        ${
          money
            ? fmtMoney(Number(value || 0), cur)
            : Number(value || 0)
        }
      </div>
    </div>
  `;

  const inner = `
    <div class="doc-title">
      Loan Monthly Summary
    </div>

    <div style="font-size:12px;color:#475569;margin-bottom:14px;">
      Month:
      <b style="color:#0b2545;">
        ${monthTitle || ""}
      </b>
    </div>

    <div class="summary-box">
      ${stat("Total Loans", report.total_loans)}
      ${stat("New Loans", report.new_loans)}
      ${stat("Renewals", report.renewal_loans)}
    </div>

    <div class="summary-box">
      ${stat(
        "Total Money Lent",
        report.total_money_lent,
        true
      )}
      ${stat(
        "Total Interest",
        report.total_interest,
        true
      )}
      ${stat(
        "Total Renewal Amount",
        renewalTotal,
        true
      )}
    </div>

    <div class="section-title">
      Loan Details
    </div>

    <table>
      <thead>
        <tr>
          <th>Sl.No</th>
          <th>Date</th>
          <th>Customer</th>
          <th>Loan No.</th>
          <th>Type</th>
          <th>Principal</th>
          <th>Interest</th>
          <th>Renewal Amount</th>
        </tr>
      </thead>

      <tbody>
        ${
          tableRows ||
          `
            <tr>
              <td
                colspan="8"
                style="text-align:center;color:#64748b;"
              >
                No loans found for this month.
              </td>
            </tr>
          `
        }

        ${
          rows.length
            ? `
              <tr style="font-weight:800;border-top:2px solid #0b2545;">
                <td colspan="5">
                  MONTH TOTAL
                </td>

                <td class="amt">
                  ${fmtMoney(
                    rows.reduce(
                      (sum, row) =>
                        sum + Number(row.principal || 0),
                      0
                    ),
                    cur
                  )}
                </td>

                <td class="amt">
                  ${fmtMoney(
                    report.total_interest || 0,
                    cur
                  )}
                </td>

                <td class="amt">
                  ${fmtMoney(
                    renewalTotal,
                    cur
                  )}
                </td>
              </tr>
            `
            : ""
        }
      </tbody>
    </table>

    <div style="
      margin-top:12px;
      font-size:10px;
      color:#64748b;
      line-height:1.5;
    ">
      Renewal interest includes only the new interest generated
      by the renewed loan. Renewal Amount shows only the fresh
      cash given to the customer.
    </div>
  `;

  printWindow(
    wrap(
      `FinLend Loan Monthly Summary - ${monthTitle || ""}`,
      inner,
      business,
      settings
    )
  );
}


// FINLEND_STATEMENT_V1
export function printStatementOfAccount(report, business, settings) {
  if (!report) return;
  const esc = value => String(value ?? "").replace(/[&<>"']/g, char =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
  const money = value => esc(fmtMoney(value || 0, settings?.currency || "₹"));
  const df = settings?.date_format;
  const rows = report.rows.map((row, index) => `<tr>
    <td>${index + 1}</td><td>${esc(fmtDate(row.date, df))}</td>
    <td>${esc(row.particulars)}</td><td>${esc(row.reference)}</td>
    <td class="amt">${row.debit ? money(row.debit) : "—"}</td>
    <td class="amt">${row.credit ? money(row.credit) : "—"}</td>
    <td class="amt">${money(row.balance)}</td></tr>`).join("");
  const inner = `
    <div class="doc-title">Statement of Account</div>
    <p>${esc(report.scope === "customer" ? report.customer_name : "Business")}
    · ${esc(fmtDate(report.start_date, df))} to ${esc(fmtDate(report.end_date, df))}</p>
    <p style="font-size:10px;">${esc(report.note)}</p>
    <table><thead><tr><th>Sl.No</th><th>Date</th><th>Particulars</th>
    <th>Reference</th><th>Debit</th><th>Credit</th><th>${esc(report.balance_label)}</th>
    </tr></thead><tbody>
    <tr><td colspan="6">Opening balance</td><td class="amt">${money(report.opening_balance)}</td></tr>
    ${rows || '<tr><td colspan="7">No transactions in this period.</td></tr>'}
    <tr style="font-weight:bold;"><td colspan="4">Closing totals</td>
    <td class="amt">${money(report.total_debit)}</td>
    <td class="amt">${money(report.total_credit)}</td>
    <td class="amt">${money(report.closing_balance)}</td></tr>
    </tbody></table>`;
  printWindow(wrap("FinLend Statement of Account", inner, business, settings));
}


// FINLEND_CAPITAL_GROWTH_PRINT_V1
export function printCapitalGrowth(report, business, settings) {
  if (!report) return;

  const esc = value => String(value ?? "").replace(/[&<>"']/g, char =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char])
  );
  const money = value => esc(fmtMoney(Number(value || 0), settings?.currency || "₹"));
  const df = settings?.date_format;
  const records = Array.isArray(report.records) ? report.records : [];

  const values = [
    ["Opening Capital", report.configured ? money(report.opening_capital) : "Not set"],
    ["Additional Capital", money(report.additional_capital)],
    ["Interest Reinvested", money(report.interest_reinvested)],
    ["Capital Withdrawn", money(report.capital_withdrawn)],
    ["Current Lending Capital", report.configured ? money(report.current_capital) : "Not set"],
    ["Net Capital Growth", report.configured ? money(report.net_increase) : "—"],
    ["Growth Against Opening Capital", report.growth_percent == null
      ? "—" : esc(report.growth_percent) + "%"]
  ];

  const totals = values.map(([label, value]) => `
    <tr><td>${esc(label)}</td><td class="amt">${value}</td></tr>
  `).join("");

  const rows = records.map((record, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${record.date ? esc(fmtDate(record.date, df)) : "—"}</td>
      <td>${esc(record.notes || "—")}</td>
      <td class="amt">${money(record.amount)}</td>
      <td class="amt">${money(record.cumulative_interest)}</td>
    </tr>
  `).join("");

  const generated = new Date().toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });

  const inner = `
    <div class="doc-title">Capital Growth</div>
    <p style="font-size:10px;">Generated on: ${esc(generated)} IST</p>

    <div class="section-title">Capital Summary</div>
    <table>
      <thead><tr><th>Particulars</th><th>Amount / Growth</th></tr></thead>
      <tbody>${totals}</tbody>
    </table>

    <p style="font-size:10px;line-height:1.5;">
      Opening capital + additional capital + interest reinvested
      − capital withdrawn = current lending capital.
      Principal recycling reuses existing capital.
      Net growth includes additional capital and withdrawals.
    </p>

    <div class="section-title">Interest Reinvestment History</div>
    <table>
      <thead>
        <tr>
          <th>Sl.No</th><th>Date</th><th>Notes</th>
          <th>Interest Reinvested</th>
          <th>Total Interest Added to Capital</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5">No interest reinvestments recorded.</td></tr>'}
        <tr style="font-weight:bold;">
          <td colspan="3">Total Interest Reinvested</td>
          <td class="amt">${money(report.interest_reinvested)}</td>
          <td class="amt">${money(report.interest_reinvested)}</td>
        </tr>
      </tbody>
    </table>

    <p style="font-size:10px;line-height:1.5;margin-top:12px;">
      Capital totals use the saved opening capital, lifetime additional
      capital and withdrawal totals, and recorded interest reinvestments
      for this business. History shows cumulative interest reinvested.
      This report is not an available cash balance or net assets after loan losses.
    </p>
  `;

  printWindow(wrap("FinLend Capital Growth", inner, business, settings));
}
